import React, { useState, useEffect, useRef } from "react";
import { Settings, ShoppingCart, House, Globe, ChevronRight } from "lucide-react";
import i18n from "../../../i18n";

const MobilePreviewBar = ({
    isCapturing,
    imageUrl,
    isFormValid,
    handleConfirmPattern,
    localesList,
    currentLocale,
    setCurrentLocale,
    t,
}) => {
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [isLangOpen, setIsLangOpen] = useState(false);
    const settingsRef = useRef(null);

    useEffect(() => {
        if (!isSettingsOpen) {
            setIsLangOpen(false);
            return;
        }
        const handleClickOutside = (e) => {
            if (settingsRef.current && !settingsRef.current.contains(e.target)) {
                setIsSettingsOpen(false);
                setIsLangOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isSettingsOpen]);

    const handleOpenCart = () => {
        window.parent.postMessage({ type: "OPEN_CART" }, "*");
        setIsSettingsOpen(false);
    };

    const handleGoHome = () => {
        window.parent.postMessage({ type: "GO_HOME" }, "*");
        setIsSettingsOpen(false);
    };

    const handleSelectLocale = (locale) => {
        setCurrentLocale && setCurrentLocale(locale);
        i18n.changeLanguage(locale.code);
        setIsSettingsOpen(false);
    };

    const hasLanguages = localesList && localesList.length > 1;

    return (
        <div
            className="d-flex d-md-none w-100 position-absolute bottom-0 justify-content-between align-items-center gap-3 pb-3 pt-2 bg-white shadow-lg"
            style={{ zIndex: 100, borderTop: '1px solid var(--color-border)', height: '80px', paddingLeft: '24px', paddingRight: '24px' }}
        >
            {/* SETTINGS */}
            <div ref={settingsRef} className="position-relative flex-shrink-0">
                <button
                    onClick={() => setIsSettingsOpen(prev => !prev)}
                    className="shadow-sm d-flex justify-content-center align-items-center"
                    style={{
                        width: '48px', height: '48px', borderRadius: '50%',
                        backgroundColor: isSettingsOpen ? '#C5A075' : '#fff',
                        color: isSettingsOpen ? '#fff' : '#C5A075',
                        border: `1px solid ${isSettingsOpen ? '#C5A075' : 'var(--color-border)'}`,
                        cursor: 'pointer',
                        transition: 'background-color 0.2s, border-color 0.2s, color 0.2s',
                    }}
                >
                    <Settings size={20} />
                </button>

                {isSettingsOpen && (
                    <div
                        className="position-absolute shadow"
                        style={{
                            bottom: 'calc(100% + 10px)',
                            left: 0,
                            backgroundColor: '#fff',
                            border: '1px solid var(--color-border)',
                            borderRadius: '14px',
                            padding: '6px',
                            minWidth: '180px',
                            zIndex: 200,
                        }}
                    >
                        {/* Language (submenu) */}
                        {hasLanguages && (
                            <div style={{ position: 'relative' }}>
                                <button
                                    onClick={() => setIsLangOpen(prev => !prev)}
                                    className="d-flex align-items-center justify-content-between w-100"
                                    style={{
                                        padding: '11px 14px', border: 'none', borderRadius: '10px',
                                        backgroundColor: isLangOpen ? '#F7F3EE' : 'transparent',
                                        color: 'var(--color-text)', cursor: 'pointer',
                                        fontSize: '14px', fontWeight: '600',
                                        transition: 'background-color 0.15s', whiteSpace: 'nowrap',
                                    }}
                                >
                                    <div className="d-flex align-items-center gap-2">
                                        <Globe size={16} color="#C5A075" />
                                        {t('language', 'Language')}
                                    </div>
                                    <ChevronRight size={14} color="#8B7D6B"
                                        style={{ transform: isLangOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                                </button>

                                {isLangOpen && (
                                    <div style={{
                                        position: 'absolute',
                                        left: 'calc(100% + 8px)',
                                        top: 0,
                                        backgroundColor: '#fff',
                                        border: '1px solid var(--color-border)',
                                        borderRadius: '12px',
                                        padding: '6px',
                                        minWidth: '110px',
                                        zIndex: 300,
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                                    }}>
                                        {localesList.map(locale => (
                                            <button
                                                key={locale._id || locale.code}
                                                onClick={() => handleSelectLocale(locale)}
                                                className="d-flex align-items-center gap-2 w-100"
                                                style={{
                                                    padding: '9px 12px', border: 'none', borderRadius: '8px',
                                                    backgroundColor: currentLocale?.code === locale.code ? '#F7F0E8' : 'transparent',
                                                    cursor: 'pointer', fontSize: '13px', fontWeight: '700',
                                                    color: currentLocale?.code === locale.code ? '#C5A075' : 'var(--color-text)',
                                                    transition: 'all 0.15s', whiteSpace: 'nowrap',
                                                }}
                                                onMouseEnter={e => { if (currentLocale?.code !== locale.code) e.currentTarget.style.backgroundColor = '#F7F3EE'; }}
                                                onMouseLeave={e => { if (currentLocale?.code !== locale.code) e.currentTarget.style.backgroundColor = 'transparent'; }}
                                            >
                                                {locale.iconFile && (
                                                    <img src={locale.iconFile} alt="" loading="lazy"
                                                        style={{ width: '18px', height: '13px', objectFit: 'cover', borderRadius: '2px', flexShrink: 0 }} />
                                                )}
                                                {locale.displayCode || locale.code}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Divider */}
                        {hasLanguages && <div style={{ height: '1px', backgroundColor: 'var(--color-border)', margin: '4px 8px' }} />}

                        {/* My Cart */}
                        <button
                            onClick={handleOpenCart}
                            className="d-flex align-items-center gap-2 w-100"
                            style={{ padding: '11px 14px', border: 'none', borderRadius: '10px', backgroundColor: 'transparent', color: 'var(--color-text)', cursor: 'pointer', fontSize: '14px', fontWeight: '600', transition: 'background-color 0.15s', whiteSpace: 'nowrap' }}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#F7F3EE'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                            <ShoppingCart size={16} color="#C5A075" />
                            {t('my_cart', 'My Cart')}
                        </button>

                        {/* Home */}
                        <button
                            onClick={handleGoHome}
                            className="d-flex align-items-center gap-2 w-100"
                            style={{ padding: '11px 14px', border: 'none', borderRadius: '10px', backgroundColor: 'transparent', color: 'var(--color-text)', cursor: 'pointer', fontSize: '14px', fontWeight: '600', transition: 'background-color 0.15s', whiteSpace: 'nowrap' }}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#F7F3EE'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                            <House size={16} color="#C5A075" />
                            {t('go_home', 'Home')}
                        </button>
                    </div>
                )}
            </div>

            {/* CONFIRM BUTTON */}
            <button
                className="btn rounded-pill fw-bold shadow-sm flex-grow-1"
                onClick={handleConfirmPattern}
                disabled={isCapturing || !imageUrl || !isFormValid}
                style={{
                    backgroundColor: isFormValid ? '#C5A075' : '#EAE4DA',
                    color: '#fff',
                    height: '48px',
                    border: 'none',
                    fontSize: '13px',
                    opacity: isFormValid ? 1 : 0.8,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    letterSpacing: '0.5px',
                }}
            >
                {isCapturing ? <span className="spinner-border spinner-border-sm"></span> : (
                    <>
                        {isFormValid ? t('btn_confirm_design', 'CONFIRM DESIGN') : t('please_select_required', 'PLEASE SELECT REQUIRED')}
                    </>
                )}
            </button>
        </div>
    );
};

export default MobilePreviewBar;
